import { useContext, type ReactNode } from 'react';
import { SheetSurfaceContext } from '@evi/ui';

export interface ListRowProps {
  icon: ReactNode;
  label: ReactNode;
  subtitle?: ReactNode;
  /** Right-side metadata slot — typically a `<StatusPill />`. */
  meta?: ReactNode;
  onClick?: () => void;
  /** Suppress the trailing chevron (e.g. for rows that aren't navigable). */
  hideChevron?: boolean;
  /** Optional emphasis ("danger" → red text for destructive rows like Log Out). */
  tone?: 'default' | 'danger';
  iconBg?: 'surface' | 'accent-soft';
  /** Guided-tour anchor (data-tour) so a step can spotlight this row or fly
   *  EVII's orb to it — mirrors ProfilePage's CategoryRow. */
  dataTour?: string;
}

/**
 * Menu row with icon pill, label + optional subtitle stack, optional
 * meta pill, and a trailing chevron. Consumed by Profile, Rewards
 * history, and anywhere else we need a tappable nav row.
 */
export function ListRow({
  icon,
  label,
  subtitle,
  meta,
  onClick,
  hideChevron,
  tone = 'default',
  iconBg = 'surface',
  dataTour,
}: ListRowProps) {
  // Inside a glass BottomSheet, rows render as glass tiles (translucent fill +
  // hairline + shadow, no own blur — the sheet panel already blurs, so the
  // tile just pops off it). Standalone rows and non-glass sheets keep the flat
  // look. See @evi/ui SheetSurfaceContext.
  const glass = useContext(SheetSurfaceContext);
  const textColor = tone === 'danger' ? 'var(--evi-warn)' : 'var(--evi-text)';
  const iconBackground = iconBg === 'accent-soft' ? 'var(--evi-accent-soft)' : 'var(--evi-surface-2)';
  const iconColor = iconBg === 'accent-soft' ? 'var(--evi-accent)' : 'var(--evi-text)';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-tour={dataTour}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        // Phase 1 typography polish: 12/14 → 14/16, min-height
        // 56 → 60, paired with the icon container 36 → 40 below
        // and label/subtitle bumps. Submenu rows now read at a
        // comfortable scale alongside page-level typography.
        padding: glass ? '12px 14px' : '14px 16px',
        // Glass tiles: translucent fill + hairline + shadow, spaced apart so
        // each row pops off the sheet panel. No own backdrop-filter (the sheet
        // already blurs). Flat rows keep the transparent, borderless look.
        border: glass ? '1px solid var(--glass-edge)' : 'none',
        borderRadius: glass ? 14 : 0,
        background: glass ? 'var(--glass-specular), var(--glass-fill)' : 'transparent',
        boxShadow: glass ? 'var(--glass-lift)' : undefined,
        marginBottom: glass ? 8 : 0,
        color: textColor,
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        textAlign: 'left',
        minHeight: 60,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          // Glass well inside glass tiles: neutral recessed slot, no coloured
          // sticker. Flat rows keep the surface / accent-soft pill.
          background: glass ? 'var(--glass-well)' : iconBackground,
          border: glass ? '1px solid var(--glass-edge)' : undefined,
          boxShadow: glass ? 'inset 0 1px 3px rgba(0, 0, 0, 0.28)' : undefined,
          color: iconColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 16, fontWeight: 500, color: textColor }}>{label}</span>
        {subtitle && (
          <span style={{ fontSize: 13, color: 'var(--evi-text-mute)' }}>{subtitle}</span>
        )}
      </span>
      {meta ? <span>{meta}</span> : <span />}
      {!hideChevron && onClick && (
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="var(--evi-text-mute)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      )}
    </button>
  );
}
